// src/firebase/schema.js
// ============================================================
// This file documents the Firestore collections structure.
// Run seedDatabase() ONCE from AdminSettings to create initial
// admin user and sample data. Requires you to first create the
// admin account via Firebase Console (Auth) or register via UI.
// ============================================================

import {
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./config";

// ──────────────────────────────────────────────
// COLLECTION: users/{uid}
// ──────────────────────────────────────────────
// {
//   uid: string,
//   email: string,
//   displayName: string,
//   employeeId: string,          // e.g. "EMP-001", "TL-001", "ADM-001"
//   role: "admin" | "lead" | "employee",
//   department: string,          // "Engineering" | "Design" | "QA" | "HR" | "Finance" | "Operations"
//   designation: string,         // "Senior Developer", "Team Lead", etc.
//   phone: string,
//   joiningDate: string,         // "YYYY-MM-DD" — admin-selected Date of Joining (required)
//   managerId: string | null,    // uid of team lead (for employees)
//   teamId: string | null,       // uid of team lead (for employees)
//   employmentType: "full-time" | "paid-intern" | "unpaid-intern",
//   internshipStartDate: string | null,    // "YYYY-MM-DD" — equals joiningDate for interns
//   internshipEndDate: string | null,      // "YYYY-MM-DD" — joiningDate + internshipDuration months
//   internshipDuration: number | null,     // months (e.g. 3, 6)
//   internshipStatus: "active" | "completed" | "converted" | null,
//   leaveBalance: {
//     casual: number,            // remaining days
//     sick: number,
//     annual: number,
//   },
//   leaveEntitlement: {
//     casual: number,            // total days per year
//     sick: number,
//     annual: number,
//   },
//   isActive: boolean,
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: attendance/{docId}
// ──────────────────────────────────────────────
// {
//   userId: string,
//   employeeId: string,
//   displayName: string,
//   department: string,
//   date: string,                // "YYYY-MM-DD"
//   checkIn: Timestamp | null,
//   checkOut: Timestamp | null,
//   hoursWorked: number,         // calculated on check-out
//   status: "present" | "absent" | "wfh" | "leave" | "holiday",
//   mode: "office" | "remote",
//   isLate: boolean,
//   lateByMinutes: number,
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: tasks/{taskId}
// ──────────────────────────────────────────────
// {
//   title: string,
//   description: string,
//   assignedTo: string,          // uid
//   assignedToName: string,
//   assignedBy: string,          // uid
//   assignedByName: string,
//   teamId: string,              // lead uid
//   department: string,
//   priority: "high" | "medium" | "low",
//   status: "pending" | "in_progress" | "done" | "cancelled",
//   deadline: Timestamp,
//   completedAt: Timestamp | null,
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: meetings/{meetingId}
// ──────────────────────────────────────────────
// {
//   title: string,
//   agenda: string,
//   scheduledBy: string,         // uid
//   scheduledByName: string,
//   date: string,                // "YYYY-MM-DD"
//   time: string,                // "HH:MM"
//   meetLink: string,
//   audience: "all" | "leads" | string, // department name or uid list
//   attendees: string[],         // array of uids
//   status: "upcoming" | "completed" | "cancelled",
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: announcements/{announcementId}
// ──────────────────────────────────────────────
// {
//   title: string,
//   message: string,
//   priority: "high" | "medium" | "low",
//   audience: "all" | "leads" | "employees",
//   postedBy: string,            // uid
//   postedByName: string,
//   teamId: string | null,       // null = org-wide, uid = team-specific
//   readBy: string[],            // array of uids who have read it
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: leaveRequests/{requestId}
// ──────────────────────────────────────────────
// {
//   userId: string,
//   employeeId: string,
//   displayName: string,
//   department: string,
//   teamId: string,              // lead uid
//   leaveType: "casual" | "sick" | "annual",
//   fromDate: string,            // "YYYY-MM-DD"
//   toDate: string,
//   totalDays: number,
//   reason: string,
//   handoverTo: string,          // uid
//   handoverToName: string,
//   status: "pending" | "approved" | "rejected",
//   reviewedBy: string | null,   // lead/admin uid
//   reviewNote: string,
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: teamRemovalRequests/{requestId}
// Workflow: Team Lead requests removal → Admin approves/rejects
// Only Admin can finalize employee removal from team
// ──────────────────────────────────────────────
// {
//   employeeId: string,
//   employeeUid: string,
//   employeeName: string,
//   teamLeadId: string,          // uid of requesting lead
//   teamLeadName: string,
//   reason: string,              // optional removal reason
//   requestedAt: Timestamp,
//   status: "PENDING_ADMIN" | "APPROVED" | "REJECTED",
//   approvedBy: string | null,   // admin uid (null until approved/rejected)
//   approvedAt: Timestamp | null,
//   rejectionReason: string | null,
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: messages/{conversationId}/msgs/{msgId}
// conversationId = sorted join of two uids e.g. "uid1_uid2"
// ──────────────────────────────────────────────
// {
//   senderId: string,
//   senderName: string,
//   text: string,
//   createdAt: Timestamp,
//   readAt: Timestamp | null,
// }

// ──────────────────────────────────────────────
// COLLECTION: orgSettings/{singleton "config"}
// ──────────────────────────────────────────────
// {
//   orgName: string,
//   workHoursPerDay: number,
//   lateThresholdMinutes: number,
//   financialYearStart: string,  // "April"
//   leavePolicy: {
//     casual: number, sick: number, annual: number,
//     autoApproveAfterDays: number, maxCarryForward: number,
//   },
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: holidays/{holidayId}
// ──────────────────────────────────────────────
// {
//   name: string,                // "Diwali", "Republic Day", etc.
//   date: string,                // "YYYY-MM-DD"
//   type: "public" | "optional" | "restricted",
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }
//
// FIRESTORE INDEX NEEDED:
//   holidays: date ASC
//
// ──────────────────────────────────────────────
// COLLECTION: shifts/{shiftId}
// ──────────────────────────────────────────────
// {
//   name: string,                // "Morning Shift", "Night Shift A"
//   startTime: string,           // "HH:MM" 24h format
//   endTime: string,             // "HH:MM" 24h format
//   type: "morning" | "evening" | "night" | "custom",
//   description: string,
//   createdBy: string,           // uid
//   isActive: boolean,
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }
//
// ──────────────────────────────────────────────
// COLLECTION: shiftAssignments/{userId}
// Doc ID = userId for fast single-doc lookup
// ──────────────────────────────────────────────
// {
//   userId: string,
//   shiftId: string,
//   shiftName: string,           // denormalized for display
//   assignedBy: string,          // uid of admin/lead
//   assignedAt: Timestamp,
//   updatedAt: Timestamp,
// }

// ──────────────────────────────────────────────
// COLLECTION: notes/{noteId}
// Personal notes/reminders for employees
// ──────────────────────────────────────────────
// {
//   userId: string,              // uid of note creator
//   displayName: string,
//   title: string,
//   content: string,
//   category: "general" | "reminder" | "urgent" | "todo",
//   isPinned: boolean,
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }
//
// FIRESTORE INDEX NEEDED:
//   notes: (userId ASC, isPinned DESC, createdAt DESC)
//

// ──────────────────────────────────────────────
// COLLECTION: queries/{queryId}
// Team member queries/doubts posted to team lead
// ──────────────────────────────────────────────
// {
//   title: string,
//   question: string,
//   category: "technical" | "process" | "general" | "career" | "other",
//   askedBy: string,             // uid of employee
//   askedByName: string,
//   teamLeadId: string,          // uid of team lead
//   teamLeadName: string,
//   status: "open" | "answered" | "closed",
//   replies: [                   // array of reply objects
//     {
//       id: string,              // unique reply id (timestamp based)
//       repliedBy: string,       // uid
//       repliedByName: string,
//       repliedByRole: string,   // "lead" or "admin"
//       reply: string,           // reply content
//       createdAt: Timestamp,
//     }
//   ],
//   isNotified: boolean,         // whether lead has been notified
//   createdAt: Timestamp,
//   updatedAt: Timestamp,
// }
//
// FIRESTORE INDEX NEEDED:
//   queries: (teamLeadId ASC, createdAt DESC)
//   queries: (teamLeadId ASC, status ASC, createdAt DESC)
//   queries: (askedBy ASC, createdAt DESC)
//

/**
 * Creates the initial admin user document in Firestore.
 * Call this ONCE after creating the admin user via Firebase Auth.
 * @param {string} uid - The Firebase Auth UID of the admin user
 * @param {string} email - Admin email
 */
export async function createAdminProfile(uid, email) {
  await setDoc(doc(db, "users", uid), {
    uid,
    email,
    displayName: "Admin User",
    employeeId: "ADM-001",
    role: "admin",
    department: "Administration",
    designation: "HR Administrator",
    phone: "",
    joiningDate: new Date().toISOString().split("T")[0],
    managerId: null,
    teamId: null,
    leaveBalance: { casual: 12, sick: 12, annual: 21 },
    leaveEntitlement: { casual: 12, sick: 12, annual: 21 },
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Creates the default org settings document.
 */
export async function createOrgSettings() {
  await setDoc(doc(db, "orgSettings", "config"), {
    orgName: "Samriddhi Anveshana",
    workHoursPerDay: 8,
    lateThresholdMinutes: 15,
    financialYearStart: "April",
    leavePolicy: {
      casual: 12,
      sick: 12,
      annual: 21,
      autoApproveAfterDays: 3,
      maxCarryForward: 10,
    },
    updatedAt: serverTimestamp(),
  });
}
